import type { ReactNode } from "react";
import { SubmissionImage } from "../../pages/content-editing/components/SubmissionImage";
import { GlowingEffect } from "./effects/GlowingEffect";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";

/**
 * Minimal submission type that works for both admin and provider contexts
 */
export interface SubmissionCardData {
  id: string;
  title: string;
  synopsis: string | null;
  posterUrl: string | null;
  status: string;
  year?: number | null;
  releaseYear?: number | null;
  durationMinutes?: number | null;
  runtime?: number | null;
  director?: string | null;
  directorData?: unknown;
}

interface SubmissionCardProps {
  readonly submission: SubmissionCardData;
  readonly actions: ReactNode;
  readonly subtitle?: ReactNode;
}

/**
 * Shared Submission Card Component
 *
 * Used across both admin and provider dashboards with different actions and subtitles.
 *
 * @example Admin usage:
 * <SubmissionCard
 *   submission={submission}
 *   subtitle={<span>Submitted by {org} on {date}</span>}
 *   actions={<Link to="/admin/review/{id}">Review</Link>}
 * />
 *
 * @example Provider usage:
 * <SubmissionCard
 *   submission={submission}
 *   actions={<><Link to="/edit">Edit</Link><button>Delete</button></>}
 * />
 */
export function SubmissionCard({ submission, actions, subtitle }: SubmissionCardProps) {
  const getStatusVariant = (status: string): "draft" | "pending" | "approved" | "rejected" | "published" => {
    const variants: Record<string, "draft" | "pending" | "approved" | "rejected" | "published"> = {
      DRAFT: "draft",
      PENDING: "pending",
      APPROVED: "approved",
      REJECTED: "rejected",
      PUBLISHED: "published",
    };
    return variants[status] || "draft";
  };

  // Support both field naming conventions
  const year = submission.year ?? submission.releaseYear;
  const runtime = submission.durationMinutes ?? submission.runtime;

  // Extract director name from directorData JSON or use legacy field
  const getDirectorName = (): string | null => {
    if (submission.directorData && typeof submission.directorData === "object") {
      // directorData is an array: [{name: "Director Name"}]
      const data = submission.directorData as Array<{ name?: string }>;
      if (Array.isArray(data) && data.length > 0 && data[0].name) {
        return data[0].name;
      }
    }
    return submission.director ?? null;
  };
  const directorName = getDirectorName();

  return (
    <Card className="bg-transparent border-neutral-900 transition-colors relative">
      {/* Sharp border line effect */}
      <GlowingEffect
        className=""
        disabled={false}
        proximity={300}
        spread={50}
        blur={0.5}
        borderWidth={1}
        movementDuration={1.5}
      />

      {/* Diffuse glow effect */}
      <GlowingEffect
        className=""
        disabled={false}
        proximity={200}
        spread={80}
        blur={40}
        borderWidth={5}
        movementDuration={1.5}
      />

      <CardContent className="p-6 relative z-10">
        <div className="flex gap-4">
          {/* Poster Thumbnail */}
          {submission.posterUrl ? (
            <SubmissionImage
              src={submission.posterUrl}
              submissionId={submission.id}
              alt={submission.title}
              className="w-21 h-32 object-cover rounded border border-neutral-800 flex-shrink-0"
            />
          ) : (
            <div className="w-21 h-32 bg-neutral-800 rounded border border-neutral-700 flex items-center justify-center flex-shrink-0">
              <svg className="w-8 h-8 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold text-white truncate">{submission.title}</h3>
                <Badge variant={getStatusVariant(submission.status)} className="flex-shrink-0 scale-[0.75]">
                  {submission.status}
                </Badge>
              </div>

              {subtitle && <div className="text-sm text-neutral-400 mb-3">{subtitle}</div>}

              {submission.synopsis && (
                <p className="max-sm:hidden text-sm text-neutral-400 mb-3 pr-4 line-clamp-3">{submission.synopsis}</p>
              )}

              <div className="flex items-center gap-4 text-xs text-neutral-500 truncate">
                {year && <span>Year: {year}</span>}
                {runtime && <span>Runtime: {runtime}min</span>}
                {directorName && <span>Director: {directorName}</span>}
              </div>
            </div>

            {/* Action buttons - horizontal on mobile, stacked on larger */}
            <div className="flex sm:flex-col items-center gap-2 flex-shrink-0">{actions}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
